import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface Report {
  id: string;
  title: string;
  description: string;
  type: string;
  generated: string;
  author: string;
  status: string;
  lastUpdated: string;
  totalRecords: number;
  dataPoints: number;
  filters?: {
    dateRange: string;
    status: string;
    category: string;
    searchQuery: string;
  };
}

interface ReportState {
  reports: Report[];
  currentReport: Report | null;
  isLoading: boolean;
  error: string | null;
  filters: {
    dateRange: string;
    status: string;
    category: string;
    searchQuery: string;
  };
  sortBy: {
    field: keyof Report;
    direction: 'asc' | 'desc';
  };
}

interface ReportActions {
  // CRUD operations
  createReport: (report: Omit<Report, 'id' | 'generated' | 'lastUpdated'>) => Promise<void>;
  updateReport: (reportId: string, updates: Partial<Report>) => Promise<void>;
  deleteReport: (reportId: string) => Promise<void>;
  getReport: (reportId: string) => Report | undefined;
  
  // State management
  setCurrentReport: (report: Report | null) => void;
  setReports: (reports: Report[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  
  // Filtering and sorting
  setFilter: (key: keyof ReportState['filters'], value: string) => void;
  setSortBy: (field: keyof Report, direction: 'asc' | 'desc') => void;
  clearFilters: () => void;
  
  // Search and filtering
  getFilteredReports: () => Report[];
  getReportById: (reportId: string) => Report | undefined;
  getReportsByType: (type: string) => Report[];
  getReportsByStatus: (status: string) => Report[];
  
  // Mock data generation
  generateMockReports: () => void;
}

export const useReportStore = create<ReportState & ReportActions>()(
  devtools(
    (set, get) => ({
      // Initial state
      reports: [],
      currentReport: null,
      isLoading: false,
      error: null,
      filters: {
        dateRange: 'last-30-days',
        status: 'all',
        category: 'all',
        searchQuery: ''
      },
      sortBy: {
        field: 'lastUpdated',
        direction: 'desc'
      },

      // CRUD operations
      createReport: async (reportData) => {
        set({ isLoading: true, error: null });
        try {
          const newReport: Report = {
            ...reportData,
            id: Date.now().toString(),
            generated: new Date().toISOString(),
            lastUpdated: new Date().toISOString()
          };

          set((state) => ({
            reports: [...state.reports, newReport],
            isLoading: false
          }));
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create report',
            isLoading: false
          });
        }
      },

      updateReport: async (reportId, updates) => {
        set({ isLoading: true, error: null });
        try {
          set((state) => ({
            reports: state.reports.map(report =>
              report.id === reportId
                ? { ...report, ...updates, lastUpdated: new Date().toISOString() }
                : report
            ),
            currentReport: state.currentReport?.id === reportId
              ? { ...state.currentReport, ...updates, lastUpdated: new Date().toISOString() }
              : state.currentReport,
            isLoading: false
          }));
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update report',
            isLoading: false
          });
        }
      },

      deleteReport: async (reportId) => {
        set({ isLoading: true, error: null });
        try {
          set((state) => ({
            reports: state.reports.filter(report => report.id !== reportId),
            currentReport: state.currentReport?.id === reportId ? null : state.currentReport,
            isLoading: false
          }));
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete report',
            isLoading: false
          });
        }
      },

      getReport: (reportId) => {
        return get().reports.find(report => report.id === reportId);
      },

      // State management
      setCurrentReport: (report) => set({ currentReport: report }),
      setReports: (reports) => set({ reports }),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      // Filtering and sorting
      setFilter: (key, value) => {
        set((state) => ({
          filters: { ...state.filters, [key]: value }
        }));
      },

      setSortBy: (field, direction) => {
        set({ sortBy: { field, direction } });
      },

      clearFilters: () => {
        set({
          filters: {
            dateRange: 'last-30-days',
            status: 'all',
            category: 'all',
            searchQuery: ''
          }
        });
      },

      // Search and filtering
      getFilteredReports: () => {
        const { reports, filters, sortBy } = get();
        
        let filtered = reports.filter(report => {
          const matchesStatus = filters.status === 'all' || report.status === filters.status;
          const matchesCategory = filters.category === 'all' || report.type === filters.category;
          const matchesSearch = filters.searchQuery === '' || 
            report.title.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
            report.description.toLowerCase().includes(filters.searchQuery.toLowerCase());

          return matchesStatus && matchesCategory && matchesSearch;
        });

        // Sort reports
        filtered.sort((a, b) => {
          const aValue = a[sortBy.field];
          const bValue = b[sortBy.field];
          
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortBy.direction === 'asc' 
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }
          
          if (aValue < bValue) return sortBy.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortBy.direction === 'asc' ? 1 : -1;
          return 0;
        });

        return filtered;
      },

      getReportById: (reportId) => {
        return get().reports.find(report => report.id === reportId);
      },

      getReportsByType: (type) => {
        return get().reports.filter(report => report.type === type);
      },

      getReportsByStatus: (status) => {
        return get().reports.filter(report => report.status === status);
      },

      // Mock data generation
      generateMockReports: () => {
        const mockReports: Report[] = [
          {
            id: '1',
            title: 'Monthly Performance Report',
            description: 'Comprehensive analysis of monthly KPIs and metrics',
            type: 'Performance',
            generated: '2 hours ago',
            author: 'Sarah Johnson',
            status: 'active',
            lastUpdated: '2024-01-15',
            totalRecords: 1250,
            dataPoints: 45
          },
          {
            id: '2',
            title: 'Q4 Financial Summary',
            description: 'Quarterly financial performance and analysis',
            type: 'Financial',
            generated: '1 day ago',
            author: 'Mike Chen',
            status: 'active',
            lastUpdated: '2024-01-14',
            totalRecords: 890,
            dataPoints: 32
          },
          {
            id: '3',
            title: 'Customer Satisfaction Survey',
            description: 'Annual customer feedback and satisfaction metrics',
            type: 'Customer',
            generated: '3 days ago',
            author: 'Lisa Rodriguez',
            status: 'completed',
            lastUpdated: '2024-01-12',
            totalRecords: 2100,
            dataPoints: 78
          },
          {
            id: '4',
            title: 'Employee Productivity Analysis',
            description: 'Workforce productivity and efficiency metrics',
            type: 'HR',
            generated: '1 week ago',
            author: 'David Kim',
            status: 'active',
            lastUpdated: '2024-01-08',
            totalRecords: 450,
            dataPoints: 23
          },
          {
            id: '5',
            title: 'System Performance Metrics',
            description: 'IT infrastructure performance and uptime statistics',
            type: 'IT',
            generated: '2 weeks ago',
            author: 'Alex Thompson',
            status: 'on-hold',
            lastUpdated: '2024-01-01',
            totalRecords: 3200,
            dataPoints: 156
          }
        ];

        set({ reports: mockReports });
      }
    }),
    {
      name: 'report-store'
    }
  )
);

